import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Search, X } from 'lucide-react';
import RunningStrip from './RunningStrip';
import '../index.css'; // ✅ make sure global CSS (below) is imported

export default function Header() {
  const fbLink = process.env.REACT_APP_ASUS_FB_LINK || '#';
  const instagramLink = process.env.REACT_APP_ASUS_INSTAGRAM_LINK || '#';
  const linkedinLink = process.env.REACT_APP_ASUS_LINKEDIN_LINK || '#';
  const xLink = process.env.REACT_APP_ASUS_X_LINK || '#';

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Initialize searchQuery from URL
  const params = new URLSearchParams(location.search);
  const urlSearchQuery = params.get('search') || '';
  const [searchQuery, setSearchQuery] = useState(urlSearchQuery);

  const handleNavClick = (path: string) => {
    if (location.pathname === path) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  // Debounced navigation when user types in header search
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (location.pathname === '/products') {
        const currentParams = new URLSearchParams(location.search);
        const currentSearch = currentParams.get('search') || '';
        if (searchQuery.trim() !== currentSearch) {
          currentParams.set('search', searchQuery.trim());
          currentParams.set('page', '1');
          navigate({ pathname: '/products', search: currentParams.toString() });
        }
      } else if (searchQuery.trim()) {
        navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}&page=1`);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, navigate, location.pathname, location.search]);

  // Update header search when URL changes (sync with filters)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('search') || '';

    if (q !== searchQuery) {
      setSearchQuery(q);
    }
  }, [location.search, searchQuery]);

  return (
    <header className="sticky top-0 z-50 font-roboto">
      <div className="bg-gradient-to-r from-black via-amber-900 to-amber-700 shadow-md">
        <div className="max-w-8xl mx-auto px-1 sm:px-2 lg:px-2">
          <div className="flex justify-between items-center py-1.5 sm:py-2 lg:py-2.5">

            {/* Logo */}
            <Link
              to="/"
              onClick={() => handleNavClick('/')}
              className="flex items-center space-x-2 sm:space-x-2.5 lg:space-x-3"
            >
              <img
                src="../Asus_logo.png"
                alt="Asus Logo"
                className="h-7 sm:h-9 md:h-10 lg:h-11 w-auto object-contain drop-shadow-lg hover:scale-105 transition-transform"
              />
              <div className="flex flex-col leading-tight">
                <span className="font-semibold text-white text-sm sm:text-base md:text-lg lg:text-xl">
                  Parnyab Enterprises Pvt Ltd
                </span>
                <span className="text-[8px] sm:text-[9px] md:text-sm lg:text-base text-amber-300 font-medium uppercase tracking-widest whitespace-nowrap">
                  Precisely Engineered, Optimum Performance.
                </span>
              </div>
            </Link>

            {/* Social Links (500px - 1250px) */}
            <div className="hidden social-header-links ml-1 sm:ml-2 flex items-center gap-1 sm:gap-2 md:gap-2.5">
              {[{ icon: <Facebook size={24} />, link: fbLink },
              { icon: <X size={24} />, link: xLink },
              { icon: <Linkedin size={24} />, link: linkedinLink },
              { icon: <Instagram size={24} />, link: instagramLink }].map((social, idx) => (
                <a
                  key={idx}
                  href={social.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-slate-800 hover:bg-orange-600 p-1 sm:p-1.5 rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center justify-center"
                >
                  <span className="text-white">{social.icon}</span>
                </a>
              ))}
            </div>

            {/* Desktop Navigation (≥1250px) */}
            <nav className="hidden custom-nav:flex items-center space-x-4 sm:space-x-5 lg:space-x-6 text-gray-200 font-roboto font-semibold text-base sm:text-lg lg:text-xl">
              {[
                { name: 'Home', path: '/' },
                { name: 'Products', path: '/products' },
                { name: 'Catalogues', path: '/catalogues' },
                { name: 'About Us', path: '/about-us' },
                { name: 'Contact Us', path: '/contact-us' }
              ].map(({ name, path }) => (
                <Link
                  key={name}
                  to={path}
                  onClick={() => handleNavClick(path)}
                  className="relative group hover:text-amber-400 transition-all duration-300 transform hover:scale-105"
                >
                  {name}
                  <span className="absolute left-0 -bottom-0.5 w-0 h-0.5 bg-amber-400 transition-all duration-300 group-hover:w-full"></span>
                </Link>
              ))}
            </nav>


            {/* Search Bar (Desktop) */}
            <div className="hidden lg:flex relative">
              <input
                type="text"
                placeholder="Search parts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-3 py-1 rounded-full border-none focus:outline-none focus:ring-2 focus:ring-amber-400 text-gray-900 w-32 sm:w-40 lg:w-48 text-xs sm:text-sm"
              />
              <Search className="absolute left-2.5 top-1.5 h-3.5 sm:h-4 w-3.5 sm:w-4 text-gray-500" />
            </div>
          </div>

          {/* Mobile Search */}
          <div className="lg:hidden pb-1.5">
            <div className="relative">
              <input
                type="text"
                placeholder="Search parts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 border border-gray-700 rounded-full focus:ring-2 focus:ring-amber-400 focus:border-transparent bg-gray-800 text-gray-200 text-sm"
              />
              <Search className="absolute left-2.5 top-1.5 h-4 w-4 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Hidden Mobile Menu Placeholder */}
        <div
          className={`md:hidden bg-gradient-to-b from-black via-amber-900 to-amber-700 transition-all duration-300 ${isMenuOpen ? 'max-h-0 overflow-hidden' : 'max-h-0'}`}
        ></div>
      </div>


      <RunningStrip />
    </header>
  );
}
